import React from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { V2Alert } from '@uhg-abyss/web/ui/Alert';
import { V2Button } from '@uhg-abyss/web/ui/Button';
import { V2Card } from '@uhg-abyss/web/ui/Card';
import { useFormV2 } from '@uhg-abyss/web/hooks/useForm';
import {
  downloadFile,
  getDownloadableFile,
} from '@src/common/tools/downloadFile';

const ReviewHeading = styled('h2', {
  margin: 0,
  color: '$primary1',
});

const HeaderRow = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '12px',
});

const SectionStack = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const CardTitle = styled('h3', {
  marginTop: 0,
  marginBottom: '12px',
  color: '$primary1',
});

const Label = styled('p', {
  margin: 0,
  fontWeight: 600,
  color: '$black',
});

const UploadedFileName = styled('p', {
  margin: '4px 0 0',
  color: '$gray6',
  fontWeight: 600,
  wordBreak: 'break-all',
  whiteSpace: 'normal',
});
const DOWNLOAD_BUTTON_CSS = {
  marginTop: '4px',
  padding: 0,
  justifyContent: 'flex-start',
  width: 'fit-content',
  wordBreak: 'break-all',
  whiteSpace: 'normal',
};

const AddressCardStack = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

const AddressDetails = styled('p', {
  margin: '4px 0 0',
  color: '$black',
  wordBreak: 'break-all',
  whiteSpace: 'normal',
});

const DetailGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
});

const AddressBlock = styled('div', {
  paddingTop: '12px',
});

const AddressContentRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
});

const UploadedFile = (fileValue) => {
  if (!fileValue) return null;
  if (Array.isArray(fileValue)) return fileValue[0] || null;
  if (typeof fileValue === 'string') return { name: fileValue };
  return fileValue;
};

const getUploadedFileName = (fileValue) => {
  return fileValue?.name || 'No file uploaded';
};

const getSecondaryServicingAddresses = (secondaryServicing) => {
  if (!secondaryServicing) return [];

  const addressEntries = Array.isArray(secondaryServicing)
    ? secondaryServicing
    : Object.values(secondaryServicing);

  return addressEntries.filter((address) => {
    if (!address) return false;

    return Boolean(
      address.streetLine1 ||
        address.streetLine2 ||
        address.city ||
        address.state ||
        address.zipCode ||
        address.file
    );
  });
};

const formatAddress = (address) => {
  const lineOne = [address.streetLine1, address.streetLine2]
    .filter(Boolean)
    .join(', ');
  const lineTwo = [address.city, address.state, address.zipCode]
    .filter(Boolean)
    .join(', ');

  return [lineOne, lineTwo].filter(Boolean).join(' , ') || 'No address added';
};

export const UpdateReview = ({ onEdit }) => {
  const abyssForm = useFormV2();
  const w9File = UploadedFile(abyssForm.getValues('w9.file'));
  const servicingAddresses = getSecondaryServicingAddresses(
    abyssForm.getValues('secondaryServicing')
  );
  const uploadedW9Name = getUploadedFileName(w9File);

  return (
    <V2Card>
      <SectionStack>
        <HeaderRow>
          <ReviewHeading>Review your information</ReviewHeading>
          <V2Button
            variant="outline"
            onClick={onEdit}
            icon={{ icon: 'edit', position: 'leading' }}
          >
            Edit
          </V2Button>
        </HeaderRow>

        <V2Alert
          title="Review all information below carefully before submitting your attestation. You will not be able to make changes afterward."
          status="warning"
          dismissible={false}
          inline
        />

        <V2Card>
          <CardTitle>Update W-9</CardTitle>
          <Label>W-9 Form</Label>
          {getDownloadableFile(abyssForm.getValues('w9.file')) ? (
            <V2Button
              variant="text"
              onClick={() => downloadFile(abyssForm.getValues('w9.file'))}
              icon={{ icon: 'download', position: 'leading' }}
              css={DOWNLOAD_BUTTON_CSS}
            >
              {uploadedW9Name}
            </V2Button>
          ) : (
            <UploadedFileName>{uploadedW9Name}</UploadedFileName>
          )}
        </V2Card>

        <V2Card>
          <CardTitle>Additional servicing address</CardTitle>
          {servicingAddresses.length > 0 ? (
            <AddressCardStack>
              {servicingAddresses.map((address, index) => (
                <AddressBlock key={index}>
                  <AddressContentRow>
                    <DetailGroup>
                      <Label>
                        Servicing Address ({index + 1} of{' '}
                        {servicingAddresses.length})
                      </Label>
                      <AddressDetails>{formatAddress(address)}</AddressDetails>
                    </DetailGroup>

                    <DetailGroup>
                      <Label>Proof of address</Label>
                      {getDownloadableFile(address.file) ? (
                        <V2Button
                          variant="text"
                          onClick={() => downloadFile(address.file)}
                          icon={{ icon: 'download', position: 'leading' }}
                          css={DOWNLOAD_BUTTON_CSS}
                        >
                          {getUploadedFileName(UploadedFile(address.file))}
                        </V2Button>
                      ) : (
                        <UploadedFileName>
                          {getUploadedFileName(UploadedFile(address.file))}
                        </UploadedFileName>
                      )}
                    </DetailGroup>
                  </AddressContentRow>
                </AddressBlock>
              ))}
            </AddressCardStack>
          ) : (
            <UploadedFileName>
              No additional servicing addresses added
            </UploadedFileName>
          )}
        </V2Card>
      </SectionStack>
    </V2Card>
  );
};
